import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DimensionSelection } from "./types";

interface QueryState {
  measures: string[];
  rows: DimensionSelection[];
  columns: DimensionSelection[];
  dateFrom: string;
  dateTo: string;
  topN: number | null;
}

const initialState: QueryState = {
  measures: [],
  rows: [],
  columns: [],
  dateFrom: "",
  dateTo: "",
  topN: null,
};

const querySlice = createSlice({
  name: "query",
  initialState,
  reducers: {
    toggleMeasure: (state, action: PayloadAction<string>) => {
      const idx = state.measures.indexOf(action.payload);
      if (idx >= 0) state.measures.splice(idx, 1);
      else state.measures.push(action.payload);
    },
    addToRows: (state, action: PayloadAction<DimensionSelection>) => {
      const exists = state.rows.some(
        (r) =>
          r.dimensionName === action.payload.dimensionName &&
          r.hierarchyName === action.payload.hierarchyName,
      );
      if (!exists) state.rows.push(action.payload);
    },
    removeFromRows: (state, action: PayloadAction<number>) => {
      state.rows.splice(action.payload, 1);
    },
    addToColumns: (state, action: PayloadAction<DimensionSelection>) => {
      const exists = state.columns.some(
        (c) =>
          c.dimensionName === action.payload.dimensionName &&
          c.hierarchyName === action.payload.hierarchyName,
      );
      if (!exists) state.columns.push(action.payload);
    },
    removeFromColumns: (state, action: PayloadAction<number>) => {
      state.columns.splice(action.payload, 1);
    },
    setDateRange: (
      state,
      action: PayloadAction<{ dateFrom: string; dateTo: string }>,
    ) => {
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
    },
    setTopN: (state, action: PayloadAction<number | null>) => {
      state.topN = action.payload;
    },
    reorderRows: (
      state,
      action: PayloadAction<{ from: number; to: number }>,
    ) => {
      const { from, to } = action.payload;
      const item = state.rows.splice(from, 1)[0];
      state.rows.splice(to, 0, item);
    },
    reorderColumns: (
      state,
      action: PayloadAction<{ from: number; to: number }>,
    ) => {
      const { from, to } = action.payload;
      const item = state.columns.splice(from, 1)[0];
      state.columns.splice(to, 0, item);
    },
    moveToRows: (state, action: PayloadAction<DimensionSelection>) => {
      const ci = state.columns.findIndex(
        (c) =>
          c.dimensionName === action.payload.dimensionName &&
          c.hierarchyName === action.payload.hierarchyName,
      );
      if (ci >= 0) state.columns.splice(ci, 1);
      const exists = state.rows.some(
        (r) =>
          r.dimensionName === action.payload.dimensionName &&
          r.hierarchyName === action.payload.hierarchyName,
      );
      if (!exists) state.rows.push(action.payload);
    },
    moveToColumns: (state, action: PayloadAction<DimensionSelection>) => {
      const ri = state.rows.findIndex(
        (r) =>
          r.dimensionName === action.payload.dimensionName &&
          r.hierarchyName === action.payload.hierarchyName,
      );
      if (ri >= 0) state.rows.splice(ri, 1);
      const exists = state.columns.some(
        (c) =>
          c.dimensionName === action.payload.dimensionName &&
          c.hierarchyName === action.payload.hierarchyName,
      );
      if (!exists) state.columns.push(action.payload);
    },
    // ── NEW: update member filter for a row or column dimension ──────────────
    updateDimensionMembers: (
      state,
      action: PayloadAction<{
        zone: "rows" | "columns";
        index: number;
        members: string[] | undefined;
      }>,
    ) => {
      const { zone, index, members } = action.payload;
      const arr = zone === "rows" ? state.rows : state.columns;
      if (arr[index]) {
        arr[index].members = members;
      }
    },
    /** Déplie / replie un membre d'une dimension en lignes (drill-down). */
    toggleRowExpanded: (
      state,
      action: PayloadAction<{ index: number; uniqueName: string }>,
    ) => {
      const sel = state.rows[action.payload.index];
      if (!sel) return;
      const list = sel.expanded ?? [];
      sel.expanded = list.includes(action.payload.uniqueName)
        ? list.filter((u) => u !== action.payload.uniqueName)
        : [...list, action.payload.uniqueName];
    },
    resetQuery: () => initialState,
  },
});

export const {
  toggleMeasure,
  addToRows,
  removeFromRows,
  addToColumns,
  removeFromColumns,
  setDateRange,
  setTopN,
  reorderRows,
  reorderColumns,
  moveToRows,
  moveToColumns,
  updateDimensionMembers,
  toggleRowExpanded,
  resetQuery,
} = querySlice.actions;

export default querySlice.reducer;
